import Button from '../../../components/base/Button';
import { Col, FloatingLabel, Form, Row, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { ProjectCreateActions } from './data-access/+state/project-create.actions';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ProjectCreationSelectors } from './data-access/+state/project-create.selectors';
import { setToast } from '../../../components/Toasts/data-access/toast-reducer';
import { getAccountDomain, normalizeAccountDomainToProjectDomain } from '@app/shared/utils/accountDomain.utils';
import { useNavigate } from 'react-router-dom';
import { getFirstDimensionId } from 'helpers/utils';
import MandatoryPolicyUploadModal from './MandatoryPolicyUploadModal';

const CreateNew = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [overview, setOverview] = useState('');
  const [projectTitle, setProjectTitle] = useState('Create a project');
  const [isPolicyUploadModalOpen, setIsPolicyUploadModalOpen] = useState(false);
  const createSuccessHandledRef = useRef(false);
  const userId = localStorage.getItem('userId');
  const organizationId = localStorage.getItem('organizationId');
  const accountDomain = getAccountDomain();
  const activeProject = useSelector(ProjectCreationSelectors.activeProject);
  const createProjectStatus = useSelector(ProjectCreationSelectors.createProjectResponse);

  useEffect(() => {
    dispatch(ProjectCreateActions.resetCreateProjectState());
  }, [dispatch]);

  const navigateToProjectDetails = useCallback(() => {
    if (!activeProject?.projectId) return;
    const detailsUrl = new URLSearchParams({
      'dimension-id': String(getFirstDimensionId()),
      projectId: activeProject.projectId
    });
    navigate(`/project/details-tabs?${detailsUrl.toString()}`, {
      state: {
        projectTitle: activeProject.title ?? title,
        projectDomain: activeProject.domain ?? accountDomain ?? undefined,
        projectId: activeProject.projectId
      }
    });
  }, [activeProject, accountDomain, navigate, title]);

  const handleContinueAfterPolicyUpload = useCallback(() => {
    setIsPolicyUploadModalOpen(false);
    navigateToProjectDetails();
  }, [navigateToProjectDetails]);

  useEffect(() => {
    if (createProjectStatus === 'succeeded' && activeProject?.projectId && title) {
      if (createSuccessHandledRef.current) return;
      createSuccessHandledRef.current = true;
      dispatch(setToast({ show: true, title: 'Project Created', message: 'Upload a policy document to continue.' }));
      setProjectTitle(title);
      localStorage.setItem('projectId', activeProject.projectId);
      setIsPolicyUploadModalOpen(true);
    } else if (createProjectStatus === 'failed') {
      dispatch(setToast({ show: true, title: 'Failed', message: 'Failed to create project' }));
    }
  }, [createProjectStatus, activeProject, title, dispatch]);

  const onSubmit = () => {
    if (!title?.trim()) {
      dispatch(setToast({ show: true, title: 'Validation Error', message: 'Project title is required' }));
      return;
    }
    if (!accountDomain) {
      dispatch(
        setToast({
          show: true,
          title: 'Account domain missing',
          message: 'Sign out and sign in again after completing registration with a domain.'
        })
      );
      return;
    }
    if (!userId || !organizationId) {
      dispatch(setToast({ show: true, title: 'Failed', message: 'Please sign in again.' }));
      return;
    }
    createSuccessHandledRef.current = false;
    dispatch(
      ProjectCreateActions.createProjectAttempt({
        userId,
        title: title.trim(),
        overview: overview.trim(),
        domain: accountDomain,
        environment: localStorage.getItem('SELECTED_ENV') || 'DEV',
        organizationId
      })
    );
  };

  const policyModalDomain = accountDomain ?? normalizeAccountDomainToProjectDomain(activeProject?.domain ?? undefined);

  return (
    <>
      <Row className="g-0">
        <Col xs={12} className="px-0 bg-body">
          <div className="px-4 px-lg-6 pt-6 pb-9">
            <h2 className="mb-2">{projectTitle}</h2>
            {/* {accountDomain ? (
              <p className="text-body-tertiary small mb-4">
                Domain: <strong>{accountDomain}</strong>
              </p>
            ) : (
              <p className="text-danger small mb-4">Account domain not set. Re-sign in after registration.</p>
            )} */}
            <Row className="g-3 mb-6">
              <Col xs={12}>
                <FloatingLabel label="Project title">
                  <Form.Control value={title} onChange={e => setTitle(e.target.value)} required />
                </FloatingLabel>
              </Col>
              <Col xs={12}>
                <FloatingLabel label="Project overview">
                  <Form.Control
                    as="textarea"
                    style={{ height: 100 }}
                    value={overview}
                    onChange={e => setOverview(e.target.value)}
                  />
                </FloatingLabel>
              </Col>
              <Col xs={12} className="d-flex justify-content-end gap-3">
                <Button
                  variant="phoenix-primary"
                  onClick={() => navigate(userId ? `/project/${userId}/all-projects` : '/')}
                >
                  Cancel
                </Button>
                <Button variant="phoenix-primary" onClick={onSubmit} disabled={createProjectStatus === 'loading'}>
                  {createProjectStatus === 'loading' ? <Spinner size="sm" animation="grow" /> : 'Create Project'}
                </Button>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
      {activeProject?.projectId && (
        <MandatoryPolicyUploadModal
          open={isPolicyUploadModalOpen}
          projectId={activeProject.projectId}
          projectTitle={title || activeProject.title || 'Project'}
          accountDomain={policyModalDomain}
          onContinue={handleContinueAfterPolicyUpload}
        />
      )}
    </>
  );
};

export default CreateNew;
